import { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import toast from 'react-hot-toast';

import { PrimaryBlackButton, PrimaryWhiteButton } from '../../../components/Button/Buttons';
import { Card, CardHead, CardTitle, Hint, Input } from '../../../components/Layout/PagePieces';
import useCustomerChatLimit from '../../../hooks/dashboard/limits/useCustomerChatLimit';
import { CHAT_CHANGE_LABELS, CustomerChat, unitsInUsd } from '../../../models/limits/chatLimits.model';
import { formatNumber, tableFormatDateTimeNG, timeAgo } from '../../../utils/utils';

const STATE: Record<CustomerChat['level'], { label: string; tone: string }> = {
  ok: { label: 'Chatting normally', tone: 'ok' },
  soft: { label: 'Near the limit: short replies', tone: 'warn' },
  hard: { label: 'At the limit: no replies until midnight', tone: 'bad' },
};

const changeText = (c: CustomerChat['changes'][number]) => {
  if (c.what === 'CHAT_LIMIT_SET') return `${CHAT_CHANGE_LABELS[c.what]}: ${formatNumber(Number(c.after?.daily_units ?? 0))} units`;
  if (c.what === 'CHAT_USAGE_RESET') return `${CHAT_CHANGE_LABELS[c.what]} (${formatNumber(Number(c.before?.used_today ?? 0))} units)`;
  return CHAT_CHANGE_LABELS[c.what] ?? c.what;
};

// One customer's chat allowance: today's usage against it, their own
// allowance (replaces the default and the buyer bonus), and clearing today's
// usage. Every action needs a reason and is kept.
const ChatAllowanceCard = ({ userId }: { userId: string }) => {
  const { chat, saving, fetchChat, setLimit, removeLimit, resetToday } = useCustomerChatLimit();
  const [form, setForm] = useState({ units: '', expires_at: '', reason: '' });

  useEffect(() => {
    fetchChat(userId);
    // eslint-disable-next-line
  }, [userId]);

  if (!chat) return null;

  const own = chat.limits.find((l) => l.active);
  const share = chat.allowance ? Math.min(100, Math.round((chat.used_today / chat.allowance) * 100)) : 100;
  const price = chat.usd_per_million_units;
  const reason = form.reason.trim();
  const needReason = () => {
    if (reason.length >= 10) return false;
    toast.error('Write a reason first (at least 10 characters)');
    return true;
  };
  const done = (message: string) => () => {
    toast.success(message);
    setForm({ units: '', expires_at: '', reason: '' });
  };

  const source =
    chat.source === 'customer'
      ? `Their own allowance${own?.expires_at ? ` until ${tableFormatDateTimeNG(own.expires_at)}` : ''}: ${own?.reason ?? ''}`
      : chat.source === 'buyer'
        ? 'Recent buyer, so they get the buyer allowance.'
        : 'The default allowance (no recent purchase).';

  return (
    <Card>
      <CardHead>
        <CardTitle>Chat allowance</CardTitle>
        <Chip className={STATE[chat.level].tone}>{STATE[chat.level].label}</Chip>
      </CardHead>
      <Usage>
        <strong>
          {formatNumber(chat.used_today)} of {formatNumber(chat.allowance)} units used today
        </strong>
        <span>
          {share}% {unitsInUsd(chat.used_today, price)} of {unitsInUsd(chat.allowance, price).replace('≈ ', '')} on {chat.model}
        </span>
        <Bar aria-hidden>
          <i style={{ width: `${share}%` }} className={STATE[chat.level].tone} />
          <b style={{ left: `${chat.soft_ratio * 100}%` }} />
        </Bar>
      </Usage>
      <Hint>{source} Resets at midnight, Lagos time.</Hint>

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          const units = Number(form.units);
          if (!Number.isInteger(units) || units <= 0) return toast.error('Enter a whole number of units');
          if (needReason()) return;
          setLimit(
            userId,
            { daily_units: units, reason, expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null },
            done('Their allowance is set. It applies from their next message.')
          );
        }}
      >
        <label>
          <span>Their own daily allowance (units)</span>
          <Input
            type='number'
            min={1}
            step={1000}
            placeholder={String(chat.allowance)}
            value={form.units}
            onChange={(e) => setForm({ ...form, units: e.target.value })}
          />
          <small>{form.units ? unitsInUsd(Number(form.units), price) + ' a day at most' : 'Replaces the default and the buyer allowance'}</small>
        </label>
        <label>
          <span>Ends (optional)</span>
          <Input type='datetime-local' value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
          <small>Empty means until you remove it</small>
        </label>
        <label className='wide'>
          <span>Reason (needed for every change here)</span>
          <Input
            placeholder='e.g. Business customer who chats a lot'
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
          />
        </label>
        <Buttons>
          <PrimaryBlackButton type='submit' disabled={saving}>
            Set allowance
          </PrimaryBlackButton>
          {own && (
            <PrimaryWhiteButton
              type='button'
              disabled={saving}
              onClick={() => !needReason() && removeLimit(userId, own.id, reason, done('Back to the default allowance.'))}
            >
              Back to default
            </PrimaryWhiteButton>
          )}
          <PrimaryWhiteButton
            type='button'
            disabled={saving || chat.used_today === 0}
            onClick={() => !needReason() && resetToday(userId, reason, done('Today’s usage cleared. The assistant answers again.'))}
          >
            Clear today’s usage
          </PrimaryWhiteButton>
        </Buttons>
      </Form>

      {!!chat.changes.length && (
        <Changes>
          {chat.changes.slice(0, 5).map((c, i) => (
            <li key={i}>
              <span>{changeText(c)}</span>
              <span className='why'>
                {c.reason} · {c.who} · {timeAgo(c.created_at)}
              </span>
            </li>
          ))}
        </Changes>
      )}
    </Card>
  );
};

export default ChatAllowanceCard;

const Chip = styled.span`
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid var(--line);
  &.ok {
    color: var(--ok);
  }
  &.warn {
    color: #8a5a00;
    background: #fff8e6;
  }
  &.bad {
    color: var(--red);
    background: #fdecec;
  }
`;

const Usage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
  font-variant-numeric: tabular-nums;

  & span {
    font-size: 13px;
    color: var(--muted);
  }
`;

const Bar = styled.div`
  position: relative;
  height: 8px;
  margin-top: 4px;
  border-radius: 999px;
  background: var(--panel);
  overflow: hidden;

  & i {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: var(--ok);
  }
  & i.warn {
    background: #e0a100;
  }
  & i.bad {
    background: var(--red);
  }
  & b {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background: var(--ink-2);
    opacity: 0.4;
  }
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 4px;

  & label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13px;
    color: ${({ theme }) => theme.palette.grayTextColor};
  }
  & label.wide {
    grid-column: 1 / -1;
  }
  & small {
    font-size: 11px;
  }
`;

const Buttons = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Changes = styled.ul`
  list-style: none;
  margin: 0;
  padding: 12px 0 0;
  border-top: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13.5px;

  & li {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  & .why {
    font-size: 12.5px;
    color: #7a8279;
  }
`;
